import { useMemo, useState } from "react";
import { guideTopics } from "@/mocks/comicHelp";

const quickTags = ["网站打不开", "换源", "加载慢", "收藏", "搜索", "登录"];

export default function Hero() {
  const [query, setQuery] = useState("");
  const [focused, setFocused] = useState(false);

  const results = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return [];
    return guideTopics.filter(
      (t) =>
        t.label.toLowerCase().includes(q) ||
        t.title.toLowerCase().includes(q) ||
        t.intro.toLowerCase().includes(q)
    );
  }, [query]);

  const goToGuide = (id?: string) => {
    const el = document.getElementById("guide");
    if (el) el.scrollIntoView({ behavior: "smooth" });
    if (id) {
      window.setTimeout(() => {
        window.dispatchEvent(new CustomEvent("bibi-go-topic", { detail: id }));
      }, 300);
    }
  };

  return (
    <section id="top" className="relative overflow-hidden bg-background-100">
      <div className="pointer-events-none absolute inset-0">
        <div className="absolute -left-24 top-10 h-72 w-72 rounded-full bg-primary-200/40 blur-3xl"></div>
        <div className="absolute right-0 top-0 h-80 w-80 rounded-full bg-accent-200/50 blur-3xl"></div>
        <div className="absolute bottom-0 left-1/3 h-72 w-72 rounded-full bg-secondary-100/60 blur-3xl"></div>
      </div>

      <div className="container-x relative flex flex-col items-center pt-32 pb-16 text-center md:pt-40 md:pb-20">
        <span className="inline-flex items-center gap-2 rounded-full border border-primary-200 bg-primary-100/70 px-4 py-1.5 text-sm font-medium text-primary-700">
          <i className="ri-fire-line"></i>
          全网热门漫画在线阅读平台
        </span>

        <h1 className="mt-6 max-w-3xl font-heading text-3xl leading-tight text-foreground-950 md:text-5xl">
          笔笔漫画网使用问题<span className="text-primary-600">一站解决</span>
        </h1>

        <p className="mt-5 max-w-2xl text-base leading-relaxed text-foreground-700 md:text-lg">
          网站打不开、加载慢、换源失败、收藏不同步？这里汇总了笔笔漫画网的搜索、收藏、登录等常见使用问题与解决入口，帮你快速定位原因并恢复顺畅阅读。
        </p>

        <div className="relative mt-8 w-full max-w-xl">
          <div className="flex items-center gap-2 rounded-full border border-background-300 bg-background-50 py-2 pl-5 pr-2 shadow-soft focus-within:border-primary-300">
            <i className="ri-search-line text-lg text-foreground-500"></i>
            <input
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onFocus={() => setFocused(true)}
              onBlur={() => setFocused(false)}
              placeholder="搜索问题，例如：换源、打不开、加载慢…"
              className="w-full bg-transparent text-sm text-foreground-900 outline-none placeholder:text-foreground-500"
              aria-label="搜索帮助内容"
            />
            <button
              type="button"
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => goToGuide()}
              className="inline-flex shrink-0 cursor-pointer items-center gap-1 whitespace-nowrap rounded-full bg-primary-500 px-5 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
            >
              搜索
              <i className="ri-corner-down-left-line"></i>
            </button>
          </div>

          {focused && results.length > 0 && (
            <ul className="absolute inset-x-0 top-full z-30 mt-2 overflow-hidden rounded-2xl border border-background-200 bg-background-50 text-left shadow-soft">
              {results.map((r) => (
                <li key={r.id}>
                  <button
                    type="button"
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => {
                      setQuery("");
                      goToGuide(r.id);
                    }}
                    className="flex w-full items-center gap-3 px-5 py-3 text-sm text-foreground-800 transition-colors hover:bg-primary-100"
                  >
                    <span className={`flex h-8 w-8 items-center justify-center rounded-lg bg-primary-100 text-primary-700`}>
                      <i className={`${r.icon} text-base`}></i>
                    </span>
                    <span>{r.title}</span>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>

        <div className="mt-6 flex flex-wrap items-center justify-center gap-2">
          {quickTags.map((tag) => (
            <button
              key={tag}
              type="button"
              onClick={() => setQuery(tag)}
              className="cursor-pointer whitespace-nowrap rounded-full bg-background-50/80 px-4 py-1.5 text-sm text-foreground-700 ring-1 ring-background-300 transition-colors hover:bg-secondary-100 hover:text-secondary-900"
            >
              {tag}
            </button>
          ))}
        </div>

        <div className="mt-10 flex w-full max-w-2xl flex-col gap-4 sm:flex-row">
          <a
            href="#guide"
            className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-6 py-3 text-base font-medium text-background-50 transition-colors hover:bg-primary-600"
          >
            <i className="ri-tools-line"></i>
            查看使用指南
          </a>
          <a
            href="#faq"
            className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md border border-background-300 bg-background-50 px-6 py-3 text-base font-medium text-foreground-800 transition-colors hover:bg-secondary-100 hover:text-secondary-900"
          >
            <i className="ri-question-answer-line"></i>
            浏览常见问题
          </a>
        </div>
      </div>
    </section>
  );
}